import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigation } from '../navigation';
import RequestedItems from './requested-items';

type AdminRequest = {
  request_id: string;
  reference_id: string;
  queue_status: string;
  reqtype: string;
  display_name: string | null;
  rejected_reason?: string;
};

type QueueCounts = {
  pending: number;
  approved: number;
  rejected: number;
};

const REQUESTS_API_URL = 'https://pharma-amd-0-1-42.onrender.com/pharma/api/admin/pharma/requests';
const REQUEST_DELETE_API_BASE_URL = 'https://pharma-amd-0-1-42.onrender.com/pharma/api/admin/pharma/request';

type FilterType = 'pending' | 'approved' | 'rejected' | 'all';

const Notifications: React.FC = () => {
  const { navigateTo } = useNavigation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [adminRequests, setAdminRequests] = useState<AdminRequest[]>([]);
  const [queueCounts, setQueueCounts] = useState<QueueCounts>({ pending: 0, approved: 0, rejected: 0 });
  const [selectedRequest, setSelectedRequest] = useState<AdminRequest | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterType>('pending');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const activeAbortControllerRef = useRef<AbortController | null>(null);

  // Filter admin requests based on active filter
  const filteredAdminRequests = useMemo(() => {
    return adminRequests.filter(request => request.queue_status === activeFilter);
  }, [adminRequests, activeFilter]);

  const fetchAdminRequests = async (status?: FilterType) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('Authentication token not found. Please login again.');
      }

      // Build URL with status parameter if provided (not 'all')
      let url = REQUESTS_API_URL;
      if (status && status !== 'all') {
        url += `?status=${status}`;
      }

      console.log('Fetching admin requests:', url);

      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Failed to fetch admin requests.`);
      }

      const data = await response.json();
      console.log('API Response:', data);
      console.log('Response structure:', {
        hasRequests: !!data?.requests,
        requestsType: typeof data?.requests,
        requestsLength: Array.isArray(data?.requests) ? data.requests.length : 'not array',
        hasData: !!data?.data,
        dataType: typeof data?.data,
        dataLength: Array.isArray(data?.data) ? data.data.length : 'not array',
        fullKeys: Object.keys(data || {}),
        queueCounts: data?.queue_counts
      });
      
      // Try multiple possible response structures
      let requests: AdminRequest[] = [];
      if (Array.isArray(data?.requests)) {
        requests = data.requests;
      } else if (Array.isArray(data?.data)) {
        requests = data.data;
      } else if (Array.isArray(data)) {
        requests = data;
      } else if (data && typeof data === 'object') {
        // Check if the response itself is the array of requests
        const possibleRequestKeys = ['pending', 'approved', 'rejected'];
        if (possibleRequestKeys.some(key => Array.isArray(data[key]))) {
          // If response has separate arrays for each status
          requests = [];
          possibleRequestKeys.forEach(key => {
            if (Array.isArray(data[key])) {
              requests = requests.concat(data[key]);
            }
          });
        }
      }
      
      // Only update queue counts when fetching all requests
      if (!status || status === 'all') {
        const counts = data?.queue_counts || { pending: 0, approved: 0, rejected: 0 };
        setQueueCounts(counts);
      }
      
      setAdminRequests(requests);
      console.log('Successfully fetched requests:', requests.length, 'items');
      console.log('Sample request:', requests[0] || 'No requests found');
    } catch (err: any) {
      console.error('Error fetching admin requests:', err);
      setError(err?.message || 'Failed to load admin requests.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminRequests();
  }, []);

  useEffect(() => {
    return () => {
      activeAbortControllerRef.current?.abort();
    };
  }, []);

  const deleteRequest = async (request: AdminRequest) => {
    try {
      setIsDeleting(true);
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const url = `${REQUEST_DELETE_API_BASE_URL}/${request.reqtype}/${request.request_id}`;
      console.log('Deleting request:', url);

      const response = await fetch(url, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Failed to delete request.`);
      }

      // Remove deleted request from state
      setAdminRequests(prev => prev.filter(r => r.request_id !== request.request_id));
      setQueueCounts(prev => ({
        ...prev,
        [request.queue_status]: Math.max(0, prev[request.queue_status as keyof QueueCounts] - 1)
      }));
      setDeleteConfirmId(null);
    } catch (err: any) {
      console.error('Error deleting request:', err);
      alert(err?.message || 'Failed to delete request.');
    } finally {
      setIsDeleting(false);
    }
  };

  const isMobile = useMemo(() => window.innerWidth < 768, []);

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#ffffff',
        padding: '0',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(59, 130, 246, 0.2)',
          borderTop: '3px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem auto'
        }}></div>
        <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>Loading admin requests...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#ffffff',
        padding: '2rem',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '8px',
          padding: '1rem',
          color: '#dc2626',
          fontSize: '0.9rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      padding: '0',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Navigation Bar Header - Full Width */}
      <div style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        padding: isMobile ? '0.75rem 1rem' : '0.75rem 1.5rem',
        boxShadow: '0 2px 10px rgba(16, 185, 129, 0.2)',
        position: 'sticky',
        top: '0',
        zIndex: '1000',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center'
      }}>
        {/* Back Button - Absolute Left Corner */}
        <button
          onClick={() => navigateTo('pharma-homepage')}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            padding: '0.5rem 0.75rem',
            fontSize: '0.8rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            marginRight: '1rem',
            position: 'relative',
            overflow: 'hidden',
            minWidth: '40px'
          }}
          onMouseEnter={(e) => {
            const target = e.currentTarget as HTMLButtonElement;
            if (target) {
              target.style.background = 'rgba(255, 255, 255, 0.3)';
              target.style.transform = 'translateY(-2px) scale(1.05)';
              target.style.boxShadow = '0 6px 20px rgba(255, 255, 255, 0.3)';
              target.style.minWidth = '90px';
              
              // Animate the text appearance
              const textElement = target.querySelector('.back-text') as HTMLSpanElement;
              if (textElement) {
                textElement.style.opacity = '1';
                textElement.style.transform = 'translateX(0)';
              }
            }
          }}
          onMouseLeave={(e) => {
            const target = e.currentTarget as HTMLButtonElement;
            if (target) {
              target.style.background = 'rgba(255, 255, 255, 0.2)';
              target.style.transform = 'translateY(0) scale(1)';
              target.style.boxShadow = 'none';
              target.style.minWidth = '40px';
              
              // Animate the text disappearance
              const textElement = target.querySelector('.back-text') as HTMLSpanElement;
              if (textElement) {
                textElement.style.opacity = '0';
                textElement.style.transform = 'translateX(-10px)';
              }
            }
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <path d="M19 12H5"/>
            <path d="M12 19l-7-7 7-7"/>
          </svg>
          <span 
            className="back-text"
            style={{
              opacity: '0',
              transform: 'translateX(-10px)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              whiteSpace: 'nowrap',
              position: 'absolute',
              left: '32px'
            }}
          >
            Back
          </span>
        </button>
        
        {/* Centered Content */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          height: 'auto',
          flex: 1
        }}>
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '0.125rem',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
            }}>
              Admin Requests
            </h1>
            <p style={{
              fontSize: '0.75rem',
              color: 'rgba(255, 255, 255, 0.9)',
              margin: 0,
              fontWeight: '500'
            }}>
              View and manage admin request details
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '1rem' : '2rem'
      }}>

        
        {/* Breadcrumb Filters */}
        <div style={{
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0'
        }}>
          {/* Filter options */}
          {[
            { key: 'pending', label: 'Pending', icon: 'clock' },
            { key: 'approved', label: 'Approved', icon: 'check' },
            { key: 'rejected', label: 'Rejected', icon: 'x' }
          ].map((filter, index) => (
            <div key={filter.key} style={{
              display: 'flex',
              alignItems: 'center'
            }}>
              {/* Separator */}
              {index > 0 && (
                <div style={{
                  margin: '0 0.5rem',
                  color: '#d1d5db',
                  fontSize: '1rem',
                  animation: 'pulse 2s infinite'
                }}>
                  /
                </div>
              )}
              
              <button
                onClick={() => {
                  setActiveFilter(filter.key as FilterType);
                  setIsLoading(true);
                  fetchAdminRequests(filter.key as FilterType);
                }}
                style={{
                  background: activeFilter === filter.key
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)'
                    : 'transparent',
                  color: activeFilter === filter.key ? '#10b981' : '#6b7280',
                  border: activeFilter === filter.key
                    ? '1px solid rgba(16, 185, 129, 0.2)'
                    : '1px solid transparent',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  fontWeight: activeFilter === filter.key ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderRadius: '6px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget && activeFilter !== filter.key) {
                    e.currentTarget.style.background = 'rgba(16, 185, 129, 0.05)';
                    e.currentTarget.style.color = '#10b981';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget && activeFilter !== filter.key) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#6b7280';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'transparent';
                  }
                }}
              >
                {/* Icons */}
                {filter.icon === 'clock' && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                )}
                {filter.icon === 'check' && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                    <path d="M22 4L12 14.01l-3-3"/>
                  </svg>
                )}
                {filter.icon === 'x' && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                )}
                
                {filter.label}
                
                {/* Count badge */}
                <span style={{
                  background: activeFilter === filter.key
                    ? 'rgba(16, 185, 129, 0.15)'
                    : 'rgba(107, 114, 128, 0.1)',
                  color: activeFilter === filter.key
                    ? '#059669'
                    : '#6b7280',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  padding: '0.125rem 0.375rem',
                  borderRadius: '10px',
                  minWidth: '18px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}>
                  {filter.key === 'pending' && queueCounts.pending}
                  {filter.key === 'approved' && queueCounts.approved}
                  {filter.key === 'rejected' && queueCounts.rejected}
                </span>
                
                {/* Active indicator */}
                {activeFilter === filter.key && (
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '20px',
                    height: '2px',
                    background: 'linear-gradient(90deg, #10b981, #059669)',
                    borderRadius: '1px',
                    animation: 'slideIn 0.3s ease-out'
                  }}></div>
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Add CSS animations */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes slideIn {
              from {
                width: 0;
                opacity: 0;
              }
              to {
                width: 20px;
                opacity: 1;
              }
            }
            
            @keyframes pulse {
              0%, 100% {
                opacity: 0.5;
              }
              50% {
                opacity: 1;
              }
            }
          `
        }} />

        {/* Admin Requests Section */}
        {filteredAdminRequests.length > 0 ? (
          <div style={{ marginTop: '2rem' }}>
            <h3 style={{
              fontSize: '1.2rem',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#3b82f6'
              }}></div>
              Admin Requests ({filteredAdminRequests.length})
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '1rem',
              }}
            >
              {filteredAdminRequests.map((request, index) => (
                <div
                  key={request.request_id}
                  onClick={() => {
                    setSelectedRequest(request);
                  }}
                  style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '1.25rem',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease',
                    animation: `fadeIn 0.3s ease-out ${index * 0.1}s both`,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      e.currentTarget.style.borderColor = '#10b981';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }
                  }}
                >
                  {/* Display Name */}
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '0.75rem',
                    minHeight: '1.5rem'
                  }}>
                    {request.display_name || (
                      <span style={{ 
                        color: '#9ca3af', 
                        fontStyle: 'italic',
                        fontSize: '0.9rem'
                      }}>
                        No display name
                      </span>
                    )}
                  </div>

                  {/* Request Type */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.75rem'
                  }}>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: '#6b7280', 
                      fontWeight: '500' 
                    }}>
                      Type:
                    </span>
                    <div style={{
                      padding: '0.25rem 0.625rem',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: '#1d4ed8',
                      textTransform: 'capitalize',
                      display: 'inline-block'
                    }}>
                      {request.reqtype.replace(/_/g, ' ')}
                    </div>
                  </div>

                  {/* Status */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: '#6b7280', 
                      fontWeight: '500' 
                    }}>
                      Status:
                    </span>
                    <div style={{
                      padding: '0.25rem 0.625rem',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      background: request.queue_status === 'pending' 
                        ? 'rgba(245, 158, 11, 0.1)' 
                        : request.queue_status === 'approved'
                        ? 'rgba(34, 197, 94, 0.1)'
                        : 'rgba(239, 68, 68, 0.1)',
                      color: request.queue_status === 'pending' 
                        ? '#d97706' 
                        : request.queue_status === 'approved'
                        ? '#16a34a'
                        : '#dc2626',
                      textTransform: 'capitalize',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <div style={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        background: request.queue_status === 'pending' 
                          ? '#f59e0b' 
                          : request.queue_status === 'approved'
                          ? '#10b981'
                          : '#ef4444'
                      }}></div>
                      {request.queue_status}
                    </div>
                  </div>

                  {/* Rejected Reason */}
                  {request.queue_status === 'rejected' && request.rejected_reason && (
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      background: 'rgba(239, 68, 68, 0.05)',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      borderRadius: '6px'
                    }}>
                      <div style={{
                        fontSize: '0.7rem',
                        color: '#dc2626',
                        fontWeight: '600',
                        marginBottom: '0.25rem'
                      }}>
                        Rejection Reason:
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#7f1d1d'
                      }}>
                        {request.rejected_reason}
                      </div>
                    </div>
                  )}

                  {/* Request ID (subtle) */}
                  <div style={{
                    marginTop: '0.75rem',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid #f3f4f6',
                    fontSize: '0.65rem',
                    color: '#9ca3af',
                    fontFamily: 'monospace',
                    textAlign: 'right'
                  }}>
                    ID: {request.request_id}
                  </div>

                  {/* Delete Button */}
                  <div style={{
                    marginTop: '0.75rem',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.5rem'
                  }}>
                    {deleteConfirmId === request.request_id ? (
                      <>
                        <span style={{
                          fontSize: '0.75rem',
                          color: '#6b7280',
                          alignSelf: 'center'
                        }}>
                          Are you sure?
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteRequest(request);
                          }}
                          disabled={isDeleting}
                          style={{
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.7rem',
                            fontWeight: '500',
                            cursor: isDeleting ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                        >
                          {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmId(null);
                          }}
                          disabled={isDeleting}
                          style={{
                            background: 'transparent',
                            color: '#6b7280',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.7rem',
                            fontWeight: '500',
                            cursor: isDeleting ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(request.request_id);
                        }}
                        style={{
                          background: 'transparent',
                          color: '#dc2626',
                          border: '1px solid #dc2626',
                          borderRadius: '4px',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.7rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#fef2f2';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#6b7280'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '1rem'
            }}>  
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                <rect x="9" y="3" width="6" height="4" rx="2"/>
              </svg>
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              {activeFilter === 'all' 
                ? 'No admin requests found'
                : `No ${activeFilter} requests found`
              }
            </div>
            <div style={{ fontSize: '0.875rem' }}>
              {activeFilter === 'all' 
                ? 'There are currently no admin requests to display.'
                : `There are currently no ${activeFilter} admin requests to display.`
              }
            </div>
            {activeFilter !== 'all' && adminRequests.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <button
                  onClick={() => {
                    setActiveFilter('all');
                    setIsLoading(true);
                    fetchAdminRequests();
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(16, 185, 129, 0.3)';
                    }
                  }}
                >
                  View All Requests
                </button>
              </div>
            )}
          </div>
        )}

        {/* Request Details Modal */}
        <RequestedItems
          selectedRequest={selectedRequest}
          onClose={() => setSelectedRequest(null)}
        />
      </div>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default Notifications;
